<template>
    <section
        :id="sectionId"
        ref="sectionEl"
        class="relative min-h-screen flex items-center justify-center text-white w-full overflow-hidden"
        :class="extraClass"
    >
        <!-- Background Video (only on desktop, lazy loaded) -->
        <div
            v-if="shouldShowVideo"
            class="absolute inset-0 w-full h-full z-0 overflow-hidden"
        >
            <video
                ref="videoEl"
                :autoplay="isVideoReady"
                muted
                loop
                playsinline
                :preload="videoPreload"
                :poster="image"
                class="absolute inset-0 w-full h-full object-cover"
                @error="handleVideoError"
                @timeupdate="handleTimeUpdate"
                @loadeddata="handleVideoLoaded"
            >
                <source :src="video" type="video/mp4" />
            </video>

            <!-- Black fade overlay for loop transition -->
            <div
                class="absolute inset-0 bg-black transition-opacity duration-700"
                :class="isFading ? 'opacity-100' : 'opacity-0'"
            ></div>
        </div>

        <!-- Background Image (fallback or mobile) -->
        <img
            v-else-if="showImage"
            :src="image"
            :alt="`${sectionId} background`"
            class="absolute inset-0 w-full h-full object-cover z-0"
            loading="lazy"
            @error="handleImageError"
        />

        <!-- Fallback gradient when both video and image fail -->
        <div
            v-else
            class="absolute inset-0 w-full h-full z-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
        ></div>

        <!-- Overlay -->
        <div :class="['absolute inset-0 z-10', overlayClass]"></div>

        <!-- Foreground Content -->
        <div class="relative z-20 w-full">
            <slot />
        </div>
    </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
    sectionId: { type: String, required: true },
    video: { type: String, default: "" },
    image: { type: String, required: true },
    overlayClass: { type: String, default: "bg-black/50" },
    extraClass: { type: String, default: "" },
});

const sectionEl = ref(null);
const videoEl = ref(null);
const isMobile = ref(false);
const isInViewport = ref(false);
const videoExists = ref(false);
const showImage = ref(false);
const isFading = ref(false);
const isVideoReady = ref(false);

// Preload metadata only initially, load full video when in viewport
const videoPreload = computed(() => isInViewport.value ? "auto" : "metadata");

// Show video only if: video exists, not mobile, and in viewport
const shouldShowVideo = computed(() =>
    props.video && videoExists.value && !isMobile.value && isInViewport.value
);

const handleVideoError = () => {
    videoExists.value = false;
    showImage.value = true;
};

const handleImageError = () => {
    showImage.value = false;
};

const handleVideoLoaded = () => {
    isVideoReady.value = true;
    if (videoEl.value) {
        videoEl.value.play().catch(() => {
            // Autoplay failed, video will show poster image
        });
    }
};

const handleTimeUpdate = () => {
    if (!videoEl.value) return;
    const current = videoEl.value.currentTime;
    const duration = videoEl.value.duration;

    // Fade to black during last 1 second for smooth loop
    if (duration - current < 1) {
        isFading.value = true;
    }
    // Fade back in during first 0.5s
    else if (current < 1) {
        isFading.value = false;
    }
};

// Detect if device is mobile
const checkIfMobile = () => {
    isMobile.value = window.innerWidth < 768; // md breakpoint
};

// Intersection Observer for lazy loading
let observer = null;
const setupIntersectionObserver = () => {
    if (!sectionEl.value) return;

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    isInViewport.value = true;
                    // Once in viewport, we can disconnect observer
                    if (observer) {
                        observer.disconnect();
                    }
                }
            });
        },
        {
            rootMargin: "200px", // Start loading 200px before section enters viewport
        }
    );

    observer.observe(sectionEl.value);
};

onMounted(async () => {
    // Check if mobile
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    // Setup intersection observer for lazy loading
    setupIntersectionObserver();

    // Check if video exists (only on desktop)
    if (props.video && !isMobile.value) {
        try {
            const res = await fetch(props.video, { method: "HEAD" });
            if (res.ok) {
                videoExists.value = true;
                return; // Video found, don't check for image
            }
        } catch (e) {
            // Video fetch failed, continue to check image
        }
    }

    // Check image (fallback or mobile)
    if (props.image) {
        try {
            const res = await fetch(props.image, { method: "HEAD" });
            if (res.ok) {
                showImage.value = true;
            }
        } catch (e) {
            // Image fetch failed, will show gradient fallback
        }
    }
});

onUnmounted(() => {
    window.removeEventListener("resize", checkIfMobile);
    if (observer) {
        observer.disconnect();
    }
});
</script>
